import { useMemo, useState } from 'react';
import { useStore } from '../store/StoreContext.jsx';
import { pqSections } from '../schema/pqSections.js';
import FormSection from '../components/FormSection.jsx';
import PresentationScreen from './PresentationScreen.jsx';
import { getMissingEmoneyFields } from '../store/emoneyRequired.js';
import { diffPq, pqFormSlice } from '../store/pqDiff.js';

/* =================================================================
   Planning workspace (/planning?leadId=...) — the planning team's
   editable view of a household after the advisor hands off.

   Three things distinguish it from the advisor's form:
   - Dense: every section in one scroll, no insights rail, no progress
     meters, no prompts. Planners read the whole record at once and
     reshape it for eMoney; the advisor-facing scaffolding is noise here.
   - Accountable: a change log lists who changed what since the handoff,
     which is what makes planning editing the live record safe (their
     own words for the alternative was "quite terrifying").
   - Anchored: the handoff snapshot (frozen at Plan Order submit) is one
     tab away, rendered through the same Classic view as the live record,
     so "what did the advisor give us" is always answerable.
   ================================================================= */

// The eMoney items planning must resolve before pushing — reuses the same
// rules that drive the Plan Order gate, so the two can never disagree.
function EmoneyPanel({ data }) {
  const missing = getMissingEmoneyFields(data);
  if (missing.length === 0) {
    return (
      <div className="planning-emoney is-clear" role="status">
        ✓ Every field eMoney needs is filled.
      </div>
    );
  }
  const jump = item => {
    const sectionId = String(item.arrayPath || item.path || '').split('.')[0];
    document.getElementById(`sec-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="planning-emoney" role="status">
      <span className="planning-emoney-count">
        {missing.length} field{missing.length === 1 ? '' : 's'} still needed for eMoney:
      </span>
      {missing.slice(0, 6).map(item => (
        <button key={item.id} type="button" className="planning-emoney-item" onClick={() => jump(item)}>
          {item.personLabel ? `${item.personLabel} · ` : ''}{item.label}
        </button>
      ))}
      {missing.length > 6 && <span className="planning-emoney-more">+{missing.length - 6} more</span>}
    </div>
  );
}

const when = t => {
  const d = new Date(t);
  return isNaN(d)
    ? ''
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// One log line. Money-ish values are left raw: the log cannot know field
// types, and a wrong $ on a date would be worse than an unformatted number.
function LogEntry({ e }) {
  const where = [e.section, e.rowName, e.label].filter(Boolean).join(' · ');
  return (
    <li className={`planning-log-entry kind-${e.kind}`}>
      <div className="planning-log-meta">
        <span className="planning-log-who">{e.who || 'Unknown'}</span>
        <span className="planning-log-when">{when(e.t)}</span>
      </div>
      <div className="planning-log-where">{where}</div>
      {e.kind === 'changed' && (
        <div className="planning-log-values">
          <span className="log-before">{e.before === '' ? '(empty)' : e.before}</span>
          <span className="log-arrow" aria-hidden="true">→</span>
          <span className="log-after">{e.after === '' ? '(empty)' : e.after}</span>
        </div>
      )}
      {e.kind === 'added' && <div className="planning-log-values"><span className="log-after">Added: {e.after}</span></div>}
      {e.kind === 'removed' && <div className="planning-log-values"><span className="log-before">Removed: {e.before}</span></div>}
    </li>
  );
}

export default function PlanningScreen() {
  const { data, autosaveStatus, saveError } = useStore();
  const [view, setView] = useState('workspace'); // 'workspace' | 'handoff'
  const [logOpen, setLogOpen] = useState(true);

  const archive = data.pqArchive || null;
  const log = Array.isArray(data.planningLog) ? data.planningLog : [];

  // Net drift from the handoff, computed live against the snapshot. This is
  // deliberately independent of the stored entries: even if the log were
  // somehow lost, "what differs from what the advisor gave us" stays
  // answerable.
  const drift = useMemo(
    () => (archive ? diffPq(archive.data, pqFormSlice(data)) : []),
    [archive, data]
  );

  const fam = data.family || {};
  const household =
    [fam.client1FirstName, fam.client1LastName].filter(Boolean).join(' ') || 'Household';

  if (view === 'handoff' && archive) {
    return (
      <div className="planning-handoff">
        <div className="planning-archive-ribbon" role="status">
          Archived copy: the PQ as {archive.by || 'the advisor'} submitted it on {when(archive.at)}.
          Nothing here is editable; the live record is one click back.
        </div>
        <PresentationScreen dataOverride={archive.data} onBack={() => setView('workspace')} />
      </div>
    );
  }

  return (
    <section className="screen is-active planning-screen">
      <div className="plan-nav no-print">
        <div className="plan-nav-brand brand" aria-label="Pure Financial Advisors — Planning Workspace">
          <span className="brand-text">Pure Financial Advisors</span>
          <span className="brand-divider" aria-hidden="true"></span>
          <span className="brand-context">Planning · {household}</span>
        </div>
        <div className="plan-nav-actions">
          <span className={`pill planning-save-pill status-${autosaveStatus}`}>
            {autosaveStatus === 'saving' ? 'Saving…'
              : autosaveStatus === 'error' ? 'Save failed'
              : autosaveStatus === 'blocked' ? 'Name required'
              : 'Saved'}
          </span>
          {archive && (
            <button type="button" className="btn btn-ghost-dark" onClick={() => setView('handoff')}>
              View as handed off
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost-dark"
            onClick={() => setLogOpen(o => !o)}
            aria-pressed={logOpen}
          >
            {logOpen ? 'Hide change log' : `Change log (${log.length})`}
          </button>
        </div>
      </div>

      {saveError && (
        <div className="save-error-banner" role="alert">
          <span className="save-error-msg">{saveError}</span>
        </div>
      )}

      <div className="planning-banner" role="status">
        {archive ? (
          <>
            <span className="planning-banner-strong">
              Handed off by {archive.by || 'the advisor'} on {when(archive.at)}.
            </span>{' '}
            {drift.length === 0
              ? 'Nothing has been changed since.'
              : `${drift.length} field${drift.length === 1 ? '' : 's'} changed since; every change is in the log.`}
          </>
        ) : (
          <>
            <span className="planning-banner-strong">No handoff snapshot yet.</span>{' '}
            The advisor has not submitted the Plan Order Form, so this is a live record
            the advisor may still be working in. Edits here are recorded in the change log.
          </>
        )}
      </div>

      <EmoneyPanel data={data} />

      <div className={'planning-body' + (logOpen ? ' log-open' : '')}>
        <form
          className="planning-main planning-dense"
          autoComplete="off"
          onSubmit={e => e.preventDefault()}
        >
          {pqSections.map((s, i) => (
            <FormSection key={s.id} section={s} num={String(i + 1).padStart(2, '0')} />
          ))}
        </form>

        {logOpen && (
          <aside className="planning-log" aria-label="Change log">
            <div className="planning-log-head">
              <span>Change log</span>
              <span className="planning-log-count">{log.length}</span>
            </div>
            {log.length === 0 ? (
              <p className="planning-log-empty">
                No changes recorded yet. Edits made here are added as they save,
                with who and when.
              </p>
            ) : (
              <ul className="planning-log-list">
                {[...log].reverse().map((e, i) => (
                  <LogEntry key={`${e.t}-${i}`} e={e} />
                ))}
              </ul>
            )}
          </aside>
        )}
      </div>
    </section>
  );
}
